// Block renderer — converts a list of block objects into email-safe HTML.
// Style language matches the existing RAHI-feed template:
//   Merriweather + #FF2101 accent, table-based, mobile responsive.

const RED = "#FF2101";
const INK = "#1C1917";
const MUTED = "#57534E";
const BG = "#f5f3f0";
const BORDER = "#E7E5E4";

const SHELL_HEAD = `<!DOCTYPE html>
<html lang="en">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>RAHI-feed</title>
<link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700;900&display=swap" rel="stylesheet" type="text/css">
<style>
  body { margin:0; padding:0; background:${BG}; -webkit-font-smoothing:antialiased; }
  table { border-collapse:collapse; }
  img { border:0; display:block; max-width:100%; height:auto; }
  a { color:${RED}; text-decoration:none; }
  .container { width:640px; max-width:100%; margin:0 auto; background:#ffffff; }
  .px { padding-left:48px; padding-right:48px; }
  @media (max-width:660px) {
    .container { width:100% !important; }
    .px { padding-left:24px !important; padding-right:24px !important; }
    .stack-col { display:block !important; width:100% !important; }
    .center-mobile { text-align:center !important; }
    .h1 { font-size:32px !important; line-height:1.1 !important; }
    .h2 { font-size:24px !important; line-height:1.2 !important; }
    .stat-num { font-size:30px !important; }
  }
</style>
</head>
<body style="margin:0;padding:32px 0;background:${BG};font-family:'Merriweather','Georgia',serif;color:${INK};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center">
<table role="presentation" class="container" width="640" cellpadding="0" cellspacing="0" border="0" style="background:#ffffff;">
`;

const SHELL_TAIL = `
</table>
</td></tr></table>
</body></html>`;

const esc = (s) =>
  (s || "")
    .toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const safeUrl = (u) => (u || "").trim();

// ── Block renderers ──
function renderHeader(d) {
  const logo = safeUrl(d.logo_url) || "https://media.beefree.cloud/pub/bfra/7nh2qnwu/zmp/we8/v59/73cce700-51e9-46c9-9cea-2ac7ad36b78c.png";
  const tag = esc(d.tagline || "");
  return `
  <tr><td class="px" style="padding-top:48px;padding-bottom:8px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
      <td valign="middle" style="width:140px;">
        <a href="https://riseagainsthungerindia.org"><img src="${logo}" width="120" alt="Rise Against Hunger India" style="display:block;"></a>
      </td>
      <td valign="middle" align="right" style="font-family:'Merriweather',Georgia,serif;color:${MUTED};font-size:11px;letter-spacing:3px;text-transform:uppercase;">
        ${tag}
      </td>
    </tr></table>
  </td></tr>`;
}

function renderHero(d) {
  const head = esc(d.headline || "");
  const sub = esc(d.subhead || "");
  const img = safeUrl(d.image_url);
  return `
  <tr><td class="px" style="padding-top:40px;padding-bottom:8px;">
    <h1 class="h1" style="margin:0;font-family:'Merriweather',Georgia,serif;font-weight:900;font-size:48px;line-height:1.05;letter-spacing:-1px;color:${INK};">
      ${head}
    </h1>
    ${sub ? `<p style="margin:16px 0 0;font-family:'Merriweather',Georgia,serif;color:${MUTED};font-size:16px;line-height:1.6;">${sub}</p>` : ""}
  </td></tr>
  ${img ? `<tr><td style="padding:32px 0 0;"><img src="${img}" alt="${esc(d.alt || head)}" style="width:100%;display:block;"></td></tr>` : ""}`;
}

function renderText(d) {
  const title = esc(d.title || "");
  // Inline linking: after HTML-escaping, convert [label](url) and bare URLs into <a>.
  const linkify = (safeText) => {
    // 1) Markdown-style [label](https://…) or [label](mailto:…) — precedence over bare URL
    let out = safeText.replace(/\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g, (_m, label, url) =>
      `<a href="${url}" target="_blank" rel="noopener" style="color:${RED};text-decoration:underline;">${label}</a>`);
    // 1b) Bold **text**
    out = out.replace(/\*\*([^*\n]+)\*\*/g, (_m, t) => `<strong>${t}</strong>`);
    // 2) Bare URLs (skip ones already inside href="…")
    out = out.replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g, (_m, pre, url) =>
      `${pre}<a href="${url}" target="_blank" rel="noopener" style="color:${RED};text-decoration:underline;">${url}</a>`);
    return out;
  };
  const body = (d.body || "")
    .split(/\n{2,}/)
    .map((p) => `<p style="margin:0 0 14px;font-family:'Merriweather',Georgia,serif;color:${INK};font-size:16px;line-height:1.75;">${linkify(esc(p)).replace(/\n/g, "<br>")}</p>`)
    .join("");
  return `
  <tr><td class="px" style="padding:32px 48px 8px;">
    ${title ? `<h2 class="h2" style="margin:0 0 12px;font-family:'Merriweather',Georgia,serif;font-weight:900;font-size:28px;line-height:1.2;color:${INK};">${title}</h2>` : ""}
    ${body}
  </td></tr>`;
}

function renderImage(d) {
  const src = safeUrl(d.src);
  if (!src) return "";
  const alt = esc(d.alt || "");
  const link = safeUrl(d.link);
  const inner = `<img src="${src}" alt="${alt}" style="width:100%;display:block;">`;
  return `
  <tr><td class="px" style="padding:24px 48px;">
    ${link ? `<a href="${link}">${inner}</a>` : inner}
    ${d.caption ? `<p style="margin:10px 0 0;font-family:'Merriweather',Georgia,serif;color:${MUTED};font-size:13px;line-height:1.5;font-style:italic;">${esc(d.caption)}</p>` : ""}
  </td></tr>`;
}

function renderStats(d) {
  const rows = Array.isArray(d.rows) ? d.rows : [];
  if (!rows.length) return "";
  const tiles = rows
    .map(
      (r) => `
    <td class="stack-col" valign="top" width="33%" style="padding:8px;">
      <div style="font-family:'Merriweather',Georgia,serif;font-weight:900;font-size:36px;line-height:1;color:${RED};" class="stat-num">${esc(r.number || "")}</div>
      <div style="margin-top:8px;font-family:'Merriweather',Georgia,serif;color:${MUTED};font-size:12px;letter-spacing:2px;text-transform:uppercase;">${esc(r.label || "")}</div>
    </td>`
    )
    .join("");
  return `
  <tr><td class="px" style="padding:24px 48px;">
    ${d.title ? `<h2 class="h2" style="margin:0 0 16px;font-family:'Merriweather',Georgia,serif;font-weight:900;font-size:24px;line-height:1.2;color:${INK};">${esc(d.title)}</h2>` : ""}
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${tiles}</tr></table>
  </td></tr>`;
}

function renderCta(d) {
  const label = esc(d.label || "Learn more");
  const url = safeUrl(d.url) || "#";
  const align = d.alignment === "left" ? "left" : d.alignment === "right" ? "right" : "center";
  return `
  <tr><td class="px" style="padding:24px 48px;" align="${align}">
    <a href="${url}" style="display:inline-block;padding:14px 28px;background:${RED};color:#ffffff;font-family:'Merriweather',Georgia,serif;font-weight:700;font-size:14px;letter-spacing:0.5px;text-transform:uppercase;text-decoration:none;">${label}</a>
  </td></tr>`;
}

function renderDivider() {
  return `
  <tr><td class="px" style="padding:24px 48px;">
    <div style="height:1px;background:${BORDER};line-height:1;font-size:1px;">&nbsp;</div>
  </td></tr>`;
}

function renderFooter(d) {
  const org = esc(d.org || "Rise Against Hunger India");
  const address = esc(d.address || "");
  return `
  <tr><td class="px" style="padding:48px 48px 56px;text-align:center;">
    <div style="height:3px;width:40px;background:${RED};margin:0 auto 24px;line-height:1;font-size:1px;">&nbsp;</div>
    <p style="margin:0;font-family:'Merriweather',Georgia,serif;color:${MUTED};font-size:13px;line-height:1.7;">
      ${org}<br>${address}
    </p>
    <p style="margin:20px 0 0;font-family:'Merriweather',Georgia,serif;color:${MUTED};font-size:11px;">
      You are receiving this because you opted in to receive updates from us.<br>
      <a href="%recipient.unsubscribe_url%" style="color:${MUTED};text-decoration:underline;">Unsubscribe</a>
    </p>
  </td></tr>`;
}

function renderRaw(d) {
  return d.html || "";
}

function renderSocial(d) {
  // Icons: hosted as 40px filled circular PNGs via a stable public CDN so all major
  // email clients (Gmail, Outlook, Apple Mail) render them reliably. Fallback to a
  // small text link if the image blocks (defensive alt text).
  const heading = esc(d.heading || "Get regular updates on our work");
  const size = Number(d.size) || 40;
  const links = [
    { key: "facebook",  url: d.facebook,  alt: "Facebook",  src: "https://cdn-icons-png.flaticon.com/512/124/124010.png" },
    { key: "x",         url: d.x,         alt: "X",         src: "https://cdn-icons-png.flaticon.com/512/5968/5968958.png" },
    { key: "linkedin",  url: d.linkedin,  alt: "LinkedIn",  src: "https://cdn-icons-png.flaticon.com/512/174/174857.png" },
    { key: "instagram", url: d.instagram, alt: "Instagram", src: "https://cdn-icons-png.flaticon.com/512/2111/2111463.png" },
    { key: "youtube",   url: d.youtube,   alt: "YouTube",   src: "https://cdn-icons-png.flaticon.com/512/1384/1384060.png" },
  ].filter(l => l.url && String(l.url).trim());
  if (links.length === 0) return "";
  const icons = links.map(l => `
      <a href="${l.url}" style="display:inline-block;margin:0 8px;" target="_blank" rel="noopener">
        <img src="${l.src}" width="${size}" height="${size}" alt="${l.alt}" style="display:inline-block;border:0;width:${size}px;height:${size}px;border-radius:50%;">
      </a>`).join("");
  return `
  <tr><td class="px" style="padding:32px 48px 8px;text-align:center;">
    <h3 style="margin:0 0 16px;font-family:'Merriweather',Georgia,serif;font-weight:900;font-size:20px;line-height:1.3;color:${INK};">${heading}</h3>
    <div style="text-align:center;line-height:0;">${icons}
    </div>
  </td></tr>`;
}

function renderCtaRow(d) {
  const title = esc(d.title || "Take Action Today");
  const tagline = esc(d.tagline || "");
  const buttons = (Array.isArray(d.buttons) ? d.buttons : []).filter(b => b && (b.label || "").trim() && (b.url || "").trim());
  if (buttons.length === 0) return "";
  // Compact inline-block buttons: each pill sizes to its label + padding and wraps
  // to a new line naturally when the container width isn't enough. Bulletproof for
  // Outlook via mso-padding-alt on an inner table. Centered as a group.
  const pills = buttons.map(b => {
    const color = (b.color || "#FF2101").trim();
    const label = esc(b.label);
    const url = safeUrl(b.url);
    return `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="display:inline-block;margin:6px;">
          <tr><td align="center" bgcolor="${color}" style="background:${color};border-radius:8px;mso-padding-alt:12px 22px;">
            <a href="${url}" target="_blank" rel="noopener"
               style="display:inline-block;padding:12px 22px;font-family:'Merriweather',Georgia,serif;font-weight:700;font-size:15px;line-height:1;color:#ffffff;text-decoration:none;border-radius:8px;white-space:nowrap;">
              ${label}
            </a>
          </td></tr>
        </table>`;
  }).join("");
  return `
  <tr><td class="px" style="padding:16px 48px 8px;">
    <h2 class="h2" style="margin:0 0 12px;font-family:'Merriweather',Georgia,serif;font-weight:900;font-size:26px;line-height:1.2;color:${INK};">
      ${title}
    </h2>
    ${tagline ? `<p style="margin:0 0 16px;padding:10px 16px;border-left:3px solid ${RED};font-family:'Merriweather',Georgia,serif;font-style:italic;color:${MUTED};font-size:15px;line-height:1.6;background:#faf7f4;">${tagline}</p>` : ""}
    <div style="text-align:center;line-height:0;font-size:0;">${pills}
    </div>
  </td></tr>`;
}

function renderTestimonial(d) {
  const quote = esc(d.quote || "");
  const name  = esc(d.name || "");
  const role  = esc(d.role || "");
  if (!quote) return "";
  return `
  <tr><td class="px" style="padding:16px 48px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
           style="background:#FAF5EE;border-left:4px solid ${RED};border-radius:6px;">
      <tr><td style="padding:22px 26px;">
        <div style="font-family:Georgia,'Merriweather',serif;font-size:36px;line-height:0;color:${RED};margin:0 0 6px;">&ldquo;</div>
        <p style="margin:0 0 14px;font-family:'Merriweather',Georgia,serif;font-style:italic;font-size:16px;line-height:1.7;color:${INK};">
          ${quote}
        </p>
        ${name ? `<p style="margin:0;font-family:'Merriweather',Georgia,serif;font-weight:700;font-size:13px;letter-spacing:1px;text-transform:uppercase;color:${MUTED};">— ${name}${role ? `, <span style="font-weight:400;text-transform:none;letter-spacing:0;font-style:italic;">${role}</span>` : ""}</p>` : ""}
      </td></tr>
    </table>
  </td></tr>`;
}

const RENDERERS = {
  header: renderHeader,
  hero: renderHero,
  text: renderText,
  image: renderImage,
  stats: renderStats,
  cta: renderCta,
  cta_row: renderCtaRow,
  testimonial: renderTestimonial,
  divider: renderDivider,
  footer: renderFooter,
  raw: renderRaw,
  social: renderSocial,
};

export function renderBlocksToHtml(blocks) {
  const inner = (blocks || []).map((b) => (RENDERERS[b.type] ? RENDERERS[b.type](b.data || {}) : "")).join("\n");
  return SHELL_HEAD + inner + SHELL_TAIL;
}

// Block factory — default data for each type
export function defaultBlock(type) {
  const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  const defaults = {
    header: { logo_url: "", tagline: "Quarterly Impact" },
    hero: { headline: "Your headline goes here", subhead: "A short supporting line that explains what this email is about.", image_url: "", alt: "" },
    text: { title: "", body: "Write your paragraph here. Double-press Enter for a new paragraph." },
    image: { src: "", alt: "", link: "", caption: "" },
    stats: { title: "", rows: [{ number: "3M", label: "Meals served" }, { number: "150K", label: "Lives reached" }, { number: "300+", label: "Partner schools" }] },
    cta: { label: "Donate now", url: "https://riseagainsthungerindia.org/donate", alignment: "center" },
    testimonial: { quote: "Share a short, real quote from a volunteer, partner or beneficiary here.", name: "Full Name", role: "Volunteer" },
    cta_row: {
      title: "Take Action Today",
      tagline: "Take the Hunger Action Pledge. Volunteer. Learn. Share. Donate. Together, we can Innovate Today and Nourish Tomorrow.",
      buttons: [
        { label: "Take the Pledge",     url: "https://riseagainsthungerindia.org/hunger-action-pledge",              color: "#22A05B" },
        { label: "Join the Packathon",  url: "https://riseagainsthungerindia.org/join-the-movement#event-register", color: "#0E8F7A" },
        { label: "Nutrition Quiz",      url: "https://riseagainsthungerindia.org/quiz",                              color: "#2563EB" },
        { label: "Volunteer",           url: "https://riseagainsthungerindia.org/join-the-movement#volunteer-form", color: "#F26B21" },
        { label: "Donate",              url: "https://riseagainsthungerindia.org/donate",                            color: "#DC2626" },
      ],
    },
    divider: {},
    footer: { org: "Rise Against Hunger India", address: "" },
    raw: { html: "<!-- paste raw HTML here -->" },
    social: {
      heading: "Get regular updates on our work",
      facebook: "https://facebook.com/RiseAgainstHungerIndia",
      x: "https://x.com/RiseIndia2030",
      linkedin: "https://in.linkedin.com/company/rise-against-hunger-india",
      instagram: "https://www.instagram.com/riseagainsthungerindia/",
      youtube: "https://www.youtube.com/channel/UCaSIpDg2zU2MDLFBN3pMgEw",
      size: 40,
    },
  };
  return { id, type, data: defaults[type] || {} };
}

export const BLOCK_LIBRARY = [
  { type: "header", label: "Header / Logo" },
  { type: "hero", label: "Hero (headline + image)" },
  { type: "text", label: "Text paragraph" },
  { type: "image", label: "Image" },
  { type: "stats", label: "Stats row (3 tiles)" },
  { type: "cta", label: "Call-to-action button" },
  { type: "cta_row", label: "CTA row (multi-button)" },
  { type: "testimonial", label: "Testimonial quote" },
  { type: "social", label: "Social icons row" },
  { type: "divider", label: "Divider line" },
  { type: "footer", label: "Footer" },
  { type: "raw", label: "Raw HTML (advanced)" },
];
